import { TaskTypeProps } from "../../../services/taskTypeService/taskTypeService.interface";
import { JGoogleIcon } from "../../../components";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { exportTaskTypeTemplateApi } from "../../../services/taskTypeService";
import { Select as ChakraReactSelect, SingleValue } from 'chakra-react-select'
import { CHAKRA_REACT_SELECT_STYLE, ChakraReactSelectValueProps } from "../../../utils/chakra-select/chakra-select";
import { useEffect, useState } from "react";
import { 
  Button, 
  Heading, 
  Td, 
  Tr, 
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Flex,
  InputGroup,
  InputLeftElement,
  Input,
  Checkbox,
  Avatar,
  Spinner,
} from "@chakra-ui/react";
import { headingTheme } from "../../../theme/components";
import taskTypeService from "../../../services/taskTypeService/taskTypeService";
import memberService from "../../../services/memberService/memberService";
import { useCustomToast } from "../../../hooks";

interface TableItemProps {
  taskType: TaskTypeProps
  rowIndex: number
  rowFrom: number
}

export default function TableItem(props: TableItemProps) {
  const { rowIndex, taskType, rowFrom } = props

  const assignMemberDisclosure = useDisclosure()
  const { t } = useTranslation()
  const { showToast } = useCustomToast()

  const downloadTemplate = useMutation(exportTaskTypeTemplateApi)
  const memberAccessListMutation = useMutation(taskTypeService.memberAccessList, {
    onSuccess: (resp) => {
      setSelectedMember(() => resp?.data?.map((x) => x?.user_id)??[])
    }
  })
  const memberPrivilegeListMutation = useMutation(memberService.list)
  const editMemberAccessMutation = useMutation(taskTypeService.editMemberAccess, {
    onSuccess: () => {
      showToast({
        status: 'success',
        description: t('tipe_tugas_assign_member_success')
      })
      assignMemberDisclosure?.onClose()
      memberAccessListMutation.mutate({ task_type_id: taskType.id })
    },
    onError: () => {
      showToast({
        status: 'error',
        description: t('tipe_tugas_assign_member_failed')
      })
    },
  })
  const editFillTaskTime = useMutation(taskTypeService.updateFillTaskTime, {
    onSuccess: () => {
      showToast({
        status: 'success',
        description: t('tipe_tugas_fill_time_success')
      })
    },
    onError: () => {
      showToast({
        status: 'error',
        description: t('tipe_tugas_fill_time_failed')
      })
    },
  })

  const fillOptions = [
    { label: t('tipe_tugas_access_no'), value: 'NONE' },
    { label: t('tipe_tugas_access_mandatory'), value: 'MANDATORY' },
    { label: t('tipe_tugas_access_optional'), value: 'OPTIONAL' },
  ]

  const [fillBeforeClockIn, setFillBeforeClockIn] = useState<SingleValue<ChakraReactSelectValueProps>>({ label: t('tipe_tugas_access_no'), value: 'NONE' })
  const [fillBeforeClockOut, setFillBeforeClockOut] = useState<SingleValue<ChakraReactSelectValueProps>>({ label: t('tipe_tugas_access_no'), value: 'NONE' })
  const [selectedMember, setSelectedMember] = useState<number[]>([])
  const [search, setSearch] = useState<string>('')

  // handle selected member, reset to selected member according to response api
  useEffect(() => {
    if (!assignMemberDisclosure.isOpen) return
    memberAccessListMutation?.data && setSelectedMember(() => memberAccessListMutation?.data?.data?.map((x) => x?.user_id)??[])
  }, [assignMemberDisclosure.isOpen, memberAccessListMutation?.data])

  // handle mutate selected member
  useEffect(() => {
    if (memberAccessListMutation?.data || !assignMemberDisclosure?.isOpen) return
    memberAccessListMutation.mutate({ task_type_id: taskType.id })
  }, [memberAccessListMutation?.data, assignMemberDisclosure?.isOpen])

  // handle cache member list data, if member list already mutate, then do not mutate again
  useEffect(() => {
    if (!assignMemberDisclosure.isOpen || (memberPrivilegeListMutation?.data?.data && memberPrivilegeListMutation?.data?.metadata)) return
    memberPrivilegeListMutation.mutate({ page: 1, show: 10000 })
  }, [assignMemberDisclosure.isOpen])

  // handle selected before clock in and clock out according to response api
  useEffect(() => {
    const selectedFillBeforeClockIn = fillOptions?.find((x) => x?.value === taskType?.input_before_absence_in)
    const selectedFillBeforeClockOut = fillOptions?.find((x) => x?.value === taskType?.input_before_absence_out)
    setFillBeforeClockIn(selectedFillBeforeClockIn??{ label: t('tipe_tugas_access_no'), value: 'NONE' })
    setFillBeforeClockOut(selectedFillBeforeClockOut??{ label: t('tipe_tugas_access_no'), value: 'NONE' })
  }, [])

  return (
    <>
      <Tr key={rowIndex}>
        <Td>{rowIndex + rowFrom}</Td>
        <Td>{taskType.code}</Td>
        <Td>{taskType.name}</Td>
        <Td>{taskType?.is_active ? t("customer_aktif") : t("customer_inaktif")}</Td>
        <Td>
          {memberAccessListMutation?.isLoading
            ? <Spinner />
            : <Heading 
                size='chakra_body_medium' 
                cursor='pointer' 
                color='primary' 
                onClick={assignMemberDisclosure.onOpen}
              >
                {t('tipe_tugas_access_applied', { count: memberAccessListMutation?.data?.data?.length??taskType?.member_access_count??0 })}
              </Heading>
          }
        </Td>
        <Td>
          <ChakraReactSelect
            useBasicStyles
            chakraStyles={CHAKRA_REACT_SELECT_STYLE}
            options={fillOptions}
            value={fillBeforeClockIn}
            isLoading={editFillTaskTime?.isLoading}
            onChange={(e) => {
              setFillBeforeClockIn(e)
              editFillTaskTime?.mutate({
                mode: "ABSEN_IN",
                type: e?.value??'',
                task_type_id: taskType?.id
              })
            }}
          />
        </Td>
        <Td>
          <ChakraReactSelect
            useBasicStyles
            chakraStyles={CHAKRA_REACT_SELECT_STYLE}
            options={fillOptions}
            value={fillBeforeClockOut}
            isLoading={editFillTaskTime?.isLoading}
            onChange={(e) => {
              setFillBeforeClockOut(e)
              editFillTaskTime?.mutate({
                mode: "ABSEN_OUT",
                type: e?.value??'',
                task_type_id: taskType?.id
              })
            }}
          />
        </Td>
        <Td>
          <Button 
            variant='j-primary' 
            leftIcon={<JGoogleIcon name='download' />}
            isLoading={downloadTemplate?.isLoading}
            onClick={() => downloadTemplate?.mutate({ task_type_id: taskType?.id?.toString(), filename: taskType?.name })}
          >
            {t('tipe_tugas_tabel_download_template')}
          </Button>
        </Td>
      </Tr>

      <Modal isOpen={assignMemberDisclosure.isOpen} onClose={assignMemberDisclosure.onClose} size='4xl'>
        <ModalOverlay />
        <ModalContent padding='0px' backgroundColor='background' borderRadius='16px'>
          <ModalCloseButton top='48px' right='48px' />
          <ModalBody padding='0px'>
            <Flex direction='column' padding='48px'>
              <Heading size='chakra_title_large' textAlign='center'>{t('tipe_tugas_assign_member')}</Heading>
              <Flex marginY='51px' gap='24px'>
                <Flex direction='column' flex='1'>
                  <Heading size='chakra_label_large'>{t('tipe_tugas_assign_member_select_title')}</Heading>
                  <Heading size='chakra_body_medium' marginTop='4px'>{t('tipe_tugas_assign_member_select_subtitle')}</Heading>
                  <InputGroup marginTop='16px'>
                    <InputLeftElement>
                      <JGoogleIcon name="search" />
                    </InputLeftElement>
                    <Input 
                      {...headingTheme.sizes.chakra_label_large} 
                      type="search" 
                      placeholder={t('tipe_tugas_assign_member_select_search')} 
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </InputGroup>
                  <Checkbox 
                    marginTop='16px'
                    isChecked={memberPrivilegeListMutation?.data?.data?.length !== 0 && selectedMember.length === memberPrivilegeListMutation?.data?.data?.length}
                    onChange={(e) => {
                      if (!memberPrivilegeListMutation?.data?.data || memberPrivilegeListMutation?.data?.data?.length <= 0) return
                      if (e.target.checked) {
                        setSelectedMember(() => ([...memberPrivilegeListMutation?.data?.data?.map((x) => x?.user?.id)]))
                      } else {
                        setSelectedMember(() => ([]))
                      }
                    }}
                  >
                    <Heading size='chakra_label_large'>{t('tipe_tugas_assign_member_select_all')}</Heading>
                  </Checkbox>
                  <Flex 
                    direction='column' 
                    height='280px' 
                    overflowY='auto'
                    marginTop='16px'
                    gap='10px'
                  >
                    {!memberPrivilegeListMutation?.data?.data && memberPrivilegeListMutation?.isLoading
                      ? <Spinner />
                      : memberPrivilegeListMutation?.data?.data
                          ?.filter((x) => search === '' ? x : x?.user?.display_name?.toLocaleLowerCase()?.includes(search?.toLocaleLowerCase()))
                          ?.map((member, memberIndex) => {
                            return (
                              <Flex 
                                key={memberIndex} 
                                alignItems='center' 
                                gap='12px'
                              >
                                <Checkbox
                                  isChecked={selectedMember.includes(member?.user?.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedMember((prev) => ([...prev, member?.user?.id]))
                                    } else {
                                      setSelectedMember((prev) => ([...prev.filter((x) => x !== member?.user?.id)]))
                                    }
                                  }}
                                />
                                <Avatar 
                                  size='sm' 
                                  height='48px' 
                                  width='48px' 
                                  src={member?.user?.photo_url}
                                  name={member?.user?.display_name}
                                />
                                <Flex direction='column' gap='4px'>
                                  <Heading size='chakra_title_medium'>{member?.user?.display_name}</Heading>
                                  <Heading size='chakra_body_medium'>{member?.user?.username}</Heading>
                                </Flex>
                              </Flex>
                            )
                          }
                        ) 
                    }
                  </Flex>
                </Flex>
                <Flex direction='column' flex='1'>
                  <Heading size='chakra_label_large'>{t('tipe_tugas_assign_member_selected_title')}</Heading>
                  <Heading size='chakra_body_medium' marginTop='4px'>{t('tipe_tugas_assign_member_selected_subtitle')}</Heading>
                  <Flex 
                    direction='column' 
                    gap='10px' 
                    marginTop='16px'
                    height='370px' 
                    overflowY='auto'
                  >
                    {memberPrivilegeListMutation?.data?.data
                      ?.filter(member => selectedMember.includes(member.user.id))
                      ?.map((member, memberIndex) => {
                        return (
                          <Flex 
                            key={memberIndex} 
                            alignItems='center' 
                            gap='12px'
                          >
                            <Avatar 
                              size='sm' 
                              height='48px' 
                              width='48px' 
                              src={member?.user?.photo_url}
                              name={member?.user?.display_name}
                            />
                            <Flex direction='column' gap='4px'>
                              <Heading size='chakra_title_medium'>{member?.user?.display_name}</Heading>
                              <Heading size='chakra_body_medium'>{member?.user?.username}</Heading>
                            </Flex>
                            <JGoogleIcon
                              name="delete"
                              marginLeft='auto'
                              color='error'
                              onClick={() => setSelectedMember((prev) => ([...prev.filter((x) => x !== member?.user?.id)]))}
                              cursor='pointer'
                            />
                          </Flex>
                        )
                      })
                    }
                  </Flex>
                </Flex>
              </Flex>
              <Flex 
                gap='16px' 
                alignItems='center' 
                justifyContent='flex-end'
              >
                <Button 
                  size='normal'
                  width='200px' 
                  variant='j-outline' 
                  onClick={assignMemberDisclosure.onClose}
                >{t('tipe_tugas_assign_member_cancel')}</Button>
                <Button 
                  size='normal'
                  width='200px' 
                  variant='j-primary' 
                  isLoading={editMemberAccessMutation?.isLoading}
                  onClick={() => {
                    editMemberAccessMutation?.mutate({
                      is_limit_access: selectedMember.length > 0 ? 1 : 0,
                      members: selectedMember,
                      task_type_id: taskType?.id
                    })
                  }}
                >{t('tipe_tugas_assign_member_save')}</Button>
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
